import {FC, useEffect, useState} from "react";
import {Box, Card, CardContent, Stack, styled, Typography, useTheme, Skeleton} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import ConstructionIcon from "@mui/icons-material/Construction";
import {useTeamProgressContext, SelectedTile} from "../../context/TeamProgressContext";
import {useSSTranslation} from "@/i18n/useSSTranslation";

interface IWidget {
	key: SelectedTile;
	value: string;
	isRedFlag: boolean;
	underConstruction: boolean;
}

interface ProgressWidgetProps {
	widget: IWidget;
	isSelected: boolean;
	onSelectWidget: (value: string) => void;
}

const useStyles = makeStyles()((theme) => ({
	tileSelected: {
		borderWidth: "3px",
		borderColor: theme.palette.primary.main,
		boxShadow: "unset"
	},
	cardContent: {
		height: "95px",
		position: "relative"
	},
	titleContainer: {
		position: "absolute",
		top: "20px"
	},
	valueContainer: {
		position: "absolute",
		bottom: "20px"
	}
}));

/**
 * Progress Widget Component
 * 
 * Matches legacy ProgressWidget exactly:
 * - 138px height, specific styling
 * - Selected state with border
 * - Red flag for warnings
 * - Under construction support
 * - Hover effects
 */
export function ProgressWidget({widget, isSelected, onSelectWidget}: ProgressWidgetProps) {
	const theme = useTheme();
	const {classes} = useStyles();
	const {t} = useSSTranslation();
	const {allSectionsLoaded} = useTeamProgressContext();
	const [enabledCard, setEnabledCard] = useState<boolean>(false);
	const {key, value, isRedFlag, underConstruction} = widget;
	const title = t(`team_progress_widget_${widget.key}` as any, "");
	const fontColor = !isRedFlag ? theme.palette.primary.main : theme.palette.error.dark;
	const regularStyle = !underConstruction ?
		{
			cursor: enabledCard ? "pointer" : "default",
			"&:hover": {
				borderColor: theme.palette.primary.main,
				boxShadow: "2px 4px 10px 0px #FF9E0029"
			}
		} : {};

	useEffect(() => {
		const sectionsLoaded = allSectionsLoaded();
		setEnabledCard(sectionsLoaded);
	}, [allSectionsLoaded]);

	const handleClickCard = () => {
		if (!underConstruction && enabledCard) onSelectWidget(key);
	};

	return (
		<CardStyled
			className={isSelected ? classes.tileSelected : undefined}
			onClick={handleClickCard}
			role="button"
			tabIndex={underConstruction ? -1 : 0}
			aria-pressed={isSelected}
			aria-label={`${title} tile${underConstruction ? " (under construction)" : ` with value ${value}`}`}
			onKeyDown={(e) => {
				if (!underConstruction && (e.key === "Enter" || e.key === " ")) {
					e.preventDefault();
					onSelectWidget(key);
				}
			}}
			sx={{
				flex: {xs: "1 1 30%", lg: "1 1 45%"},
				opacity: underConstruction || !enabledCard ? "50%" : "unset",
				...regularStyle
			}}
		>
			<CardContent className={classes.cardContent}>
				<Box className={classes.titleContainer}>
					<Typography
						variant="subtitle2"
						sx={{fontWeight: 600}}
					>
						{title.toUpperCase()}
					</Typography>
				</Box>
				{
					!underConstruction &&
					<Box className={classes.valueContainer}>
						<Typography
							variant="h2"
							sx={{fontWeight: 600}}
							color={fontColor}
						>
							{value}
						</Typography>
					</Box>
				}
				{
					underConstruction &&
					<Stack
						direction="row"
						gap={1}
						className={classes.valueContainer}
						sx={{
							alignItems: "center"
						}}
					>
						<ConstructionIcon fontSize="medium" color="primary" />
						<Typography
							variant="h6"
							sx={(theme) => ({
								color: theme.palette.primary.main
							})}
						>
							{t("team_progress_under_construction", "Under Construction").toUpperCase()}
						</Typography>
					</Stack>
				}
			</CardContent>
		</CardStyled>
	);
}

const CardStyled = styled(Card)(({theme}) => ({
	borderRadius: "8px",
	backgroundColor: theme.palette.background.paper,
	height: "138px",
	borderColor: "#A1A1A026",
	boxSizing: "border-box"
}));

/**
 * Progress Widget Skeleton
 * 
 * Loading state for the widget
 */
export function ProgressWidgetSkeleton() {
	return (
		<CardStyled sx={{flex: {xs: "1 1 30%", lg: "1 1 45%"}}}>
			<CardContent sx={{height: "95px", position: "relative"}}>
				<Box sx={{position: "absolute", top: "20px"}}>
					<Skeleton variant="text" width={120} height={24} />
				</Box>
				<Box sx={{position: "absolute", bottom: "20px"}}>
					<Skeleton variant="text" width={80} height={48} />
				</Box>
			</CardContent>
		</CardStyled>
	);
}
